export type ServerTenantTheme = {
  primaryColor: string;
  secondaryColor?: string | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  loginBackgroundUrl?: string | null;
};

export type ServerTenant = {
  key: string;
  displayName: string;
  auth0OrgId: string;
  theme: ServerTenantTheme;
};

const TIMEOUT_MS = 3000;

/**
 * Boot-time lookup of GET /api/tenants/{key} on the Public functions app (anonymous,
 * pre-login). Returns null on ANY failure — the caller falls back to the bundled
 * themes.json/subdomainMap so an API outage never blocks login for existing tenants.
 * Wire shape is the backend's PascalCase TenantDto.
 */
export const fetchServerTenant = async (subdomain: string): Promise<ServerTenant | null> => {
  const baseUrl = process.env.REACT_APP_PUBLIC_API_URL ?? process.env.REACT_APP_BASE_API_URL;
  if (!baseUrl) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(`${baseUrl}/tenants/${encodeURIComponent(subdomain)}`, {
      signal: controller.signal,
    });
    if (!res.ok) return null;

    const raw = await res.json();
    if (!raw?.Key || !raw?.Auth0OrgId || !raw?.Theme?.PrimaryColor) return null;

    return {
      key: raw.Key,
      displayName: raw.DisplayName ?? raw.Key,
      auth0OrgId: raw.Auth0OrgId,
      theme: {
        primaryColor: raw.Theme.PrimaryColor,
        secondaryColor: raw.Theme.SecondaryColor ?? null,
        logoUrl: raw.Theme.LogoUrl ?? null,
        faviconUrl: raw.Theme.FaviconUrl ?? null,
        loginBackgroundUrl: raw.Theme.LoginBackgroundUrl ?? null,
      },
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};
